import { useSettings, useStyles } from '@wix/tpa-settings/react';
import { TestSessionBanner } from '@wix/bookings-viewer-ui';
import {
  useEnvironment,
  useExperiments,
  useTranslation,
  WidgetProps,
} from '@wix/yoshi-flow-editor';
import React, { FC, useState } from 'react';
import { Toast, ToastPlacement, ToastSkin } from 'wix-ui-tpa/cssVars';
import { WidgetActions } from '../../../actions/actions';
import { setPresetIdFromSettings } from '../../../utils/settings/settings';
import {
  WidgetErrorStateViewModel,
  WidgetViewModel,
} from '../../../viewModel/viewModel';
import settingsParams from '../settingsParams';
import { ENABLE_FILTERING_IN_BOOKINGS_SERVICE_LIST } from '../../../consts';
import { Body } from './Body/Body';
import { Dialog } from './Dialog/Dialog';
import { EmptyState } from './EmptyState/EmptyState';
import { ErrorState } from './ErrorState/ErrorState';
import { Filters } from './Filters/Filters';
import { Header } from './Header/Header';
import { HiddenServicesForSeo } from './HiddenServicesForSeo/HiddenServicesForSeo';
import { AllBreakpointsStylesProvider } from './hooks/useAllBreakpointsStyles';
import { WidgetActionsProvider } from './hooks/useWidgetActions';
import { WidgetViewModelProvider } from './hooks/useWidgetViewModel';
import { classes, st } from './Widget.st.css';

export type ControllerProps = {
  widgetViewModel: WidgetViewModel;
  widgetErrorStateViewModel?: WidgetErrorStateViewModel;
  widgetActions: WidgetActions;
  isTestSession?: boolean;
};

export const Widget: FC<WidgetProps<ControllerProps>> = (props) => {
  const {
    widgetActions,
    widgetViewModel,
    widgetErrorStateViewModel,
    isTestSession,
  } = props;

  if (widgetErrorStateViewModel) {
    return (
      <WidgetContainer isTestSession={isTestSession}>
        <ErrorState viewModel={widgetErrorStateViewModel} />;
      </WidgetContainer>
    );
  }

  return widgetActions && widgetViewModel ? (
    <WidgetRenderer {...props} />
  ) : null;
};

const WidgetContainer: React.FC<{ isTestSession?: boolean }> = ({
  children,
  isTestSession,
}) => {
  const { isRTL, isMobile } = useEnvironment();
  const { t } = useTranslation();

  return (
    <div
      data-hook="widget"
      className={st(classes.root, {
        isRTL,
        isMobile,
      })}
    >
      {isTestSession && (
        <TestSessionBanner text={t('app.widget.test-session-banner')} />
      )}
      {children}
    </div>
  );
};

const WidgetRenderer: FC<WidgetProps<ControllerProps>> = (props) => {
  const { widgetViewModel, widgetActions } = props;
  const { headerViewModel, services, seo, errorMessage } = widgetViewModel;
  const { isMobile } = useEnvironment();
  const settings = useSettings();
  const styles = useStyles();
  const { experiments } = useExperiments();

  const [stylesForAllBreakpoints] = useState(() =>
    styles.getStylesForAllBreakpoints(),
  );
  const isFilteringEnabled = experiments.enabled(
    ENABLE_FILTERING_IN_BOOKINGS_SERVICE_LIST,
  );

  setPresetIdFromSettings(settings.get(settingsParams.presetId));

  return (
    <WidgetContainer isTestSession={props.isTestSession}>
      <AllBreakpointsStylesProvider value={stylesForAllBreakpoints}>
        <WidgetViewModelProvider value={widgetViewModel}>
          <WidgetActionsProvider value={widgetActions}>
            <Dialog
              widgetViewModel={widgetViewModel}
              widgetActions={widgetActions}
            />
            {isFilteringEnabled && !services.length && <Filters />}
            {services.length ? (
              <>
                <Header viewModel={headerViewModel} />
                {isFilteringEnabled && <Filters />}
                <Body widgetId={props.id} />
                {seo && seo.shouldListServicesFromOtherCategories ? (
                  <HiddenServicesForSeo />
                ) : null}
                {errorMessage && (
                  <Toast
                    className={classes.toast}
                    skin={ToastSkin.error}
                    data-hook="error-state-toast"
                    placement={
                      isMobile
                        ? ToastPlacement.bottomFullWidth
                        : ToastPlacement.inline
                    }
                  >
                    {errorMessage}
                  </Toast>
                )}
              </>
            ) : (
              <EmptyState />
            )}
          </WidgetActionsProvider>
        </WidgetViewModelProvider>
      </AllBreakpointsStylesProvider>
    </WidgetContainer>
  );
};
